import { Autocomplete, TextField } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { CityTaxExonerationFragment, useCityTaxExonerationsQuery } from 'src/api/api.generated'
import { useTenant } from 'src/app/hooks/context/context.hook'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'

export type CityTaxExonerationLookuProps = {
  propertyId: string
  value: CityTaxExonerationFragment | { code: string } | null
  onChange: (value: CityTaxExonerationFragment | null) => void
  disabled?: boolean
  error?: boolean
  size?: 'small' | 'medium'
}

export default function CityTaxExonerationLookup({ propertyId, value, onChange, disabled, error, size }: CityTaxExonerationLookuProps) {
  const localizeString = useLocalizeString()
  const { id: tenantId } = useTenant()

  const { data } = useCityTaxExonerationsQuery({
    variables: {
      tenantId,
      propertyId,
      usage: 'GUEST'
    },
    //fetchPolicy: 'cache-first',
  })
  const options = useMemo(() => data?.cityTaxExonerations || [], [data])
  const [localValue, setLocalValue] = useState<CityTaxExonerationFragment | null>(null)

  const localOnChange = useCallback(
    (option: CityTaxExonerationFragment | null) => {
      if (JSON.stringify(option) !== JSON.stringify(localValue)) {
        setLocalValue(option)
      }

      if (option?.code !== value?.code) {
        onChange(option)
      }
    },
    [onChange, value, localValue],
  )

  const localOptions = useMemo(() => {
    if (localValue === null || options.some((o) => o.code === localValue.code)) {
      return options
    } else {
      return [...options, localValue]
    }
  }, [options, localValue])

  useEffect(() => {
    if (value === null) {
      if (localValue !== null) {
        setLocalValue(null)
      }
    } else if ('description' in value) {
      if (JSON.stringify(value) !== JSON.stringify(localValue)) {
        setLocalValue(value)
      }
    } else if ('code' in value && value.code !== localValue?.code) {
      const newValue = options.find((o) => o.code === value.code) ?? null
      if (JSON.stringify(newValue) !== JSON.stringify(localValue)) {
        setLocalValue(newValue)
      }
    }
  }, [value, options])

  return (
    <Autocomplete //
      size={size}
      fullWidth
      value={localValue}
      onChange={(event, value) => localOnChange(value)}
      options={localOptions}
      getOptionLabel={(option) => `${option.code} - ${localizeString(option.description)}`}
      isOptionEqualToValue={(option, value) => option.code === value.code}
      renderInput={(params) => <TextField {...params} error={error} />}
      disabled={disabled}
    />
  )
}
